import { useId, useMemo, useState } from "react";

type ComboboxProps = {
  id: string;
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  maxLength?: number;
  disabled?: boolean;
};

const Combobox = ({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  helperText,
  required = false,
  maxLength,
  disabled = false,
}: ComboboxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const listboxId = useId();

  const filteredOptions = useMemo(() => {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return options;
    }

    return options.filter((option) => option.toLowerCase().includes(normalizedValue));
  }, [options, value]);

  const showOptions = isOpen && filteredOptions.length > 0;

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-myteengig-text-primary">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        required={required}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={showOptions}
        aria-controls={listboxId}
        aria-autocomplete="list"
        onFocus={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        onBlur={() => {
          window.setTimeout(() => setIsOpen(false), 100);
        }}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        className={`w-full rounded-md border border-myteengig-border px-3 py-2 text-sm outline-none focus:border-myteengig-primary ${
          disabled ? "cursor-not-allowed bg-slate-100 text-slate-500" : "bg-white text-myteengig-text-primary"
        }`}
      />
      {helperText ? <p className="mt-1 text-xs text-myteengig-text-muted">{helperText}</p> : null}

      {showOptions ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-2 max-h-56 w-full overflow-y-auto rounded-xl border border-myteengig-border bg-white p-2 shadow-lg"
        >
          {filteredOptions.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={value === option}
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(option);
                setIsOpen(false);
              }}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                value === option
                  ? "bg-myteengig-background font-semibold text-myteengig-text-primary"
                  : "text-myteengig-text-primary hover:bg-myteengig-background"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Combobox;
